import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TransactionRowText } from '@app/payments/models/payment-transaction';
import { TranslateService } from '@ngx-translate/core';
import {
  PanAppState,
  PlatformEnum,
  PlatformService,
  SESSION_STORAGE_KEYS,
} from '@panamax/app-state';
import { BrowserDimensions } from '@panamax/app-state/lib/models/platform.model';
import { MAX_INVOICE_SELECTION } from '@payments/constants/constants';
import { PaymentsBasePage } from '@payments/payments-shared/payments-base/payments-base.page';
import { TransactionRowDesktopConstants } from '@payments/payments-shared/transaction-row/desktop/transaction-row-desktop.component';
import { InvoiceIsrService } from '@payments/services/invoice-isr.service';
import { FilterBaseVM } from '@payments/services/payments-modal.service';
import { PaymentsService } from '@payments/services/payments.service';
import { UsfFilteringModalComponent } from '@shared/components/usf-filtering-modal/usf-filtering-modal.component';
import { PATHS } from '@shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';
import { Filter, InvoiceFilterIds, SortDirection } from '@usf/ngrx-payment';
import {
  PaymentTransactionsViewModel,
  SortFields,
  Status,
  WarningResponse,
} from '@usf/payment-types';
import { Observable, Subscription, combineLatest, firstValueFrom } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  skip,
  skipWhile,
  take,
} from 'rxjs/operators';
import { QuickFilter } from 'src/app/shared/components/usf-filtering-banner/model/quick-filter.model';
import { PageTitle } from 'src/app/shared/constants/page-titles.enum';
import { AllInvoicesService } from './services/all-invoices.service';
export interface AllInvoicesPageVM extends FilterBaseVM {
  transactions: PaymentTransactionsViewModel[];
  hasNextPage: boolean;
  pageNum: number;
  isLoadingTransaction: boolean;
  isSelectable: boolean;
  selectedTransactions: PaymentTransactionsViewModel[];
  paymentHasOutage?: boolean;
  isMaxTransactionsSelected: boolean;
  payCCAisOn: boolean;
  warning: WarningResponse;
}
@Component({
  selector: 'app-all-invoices',
  templateUrl: './all-invoices.page.html',
  styleUrls: ['./all-invoices.page.scss'],
})
export class AllInvoicesPage extends PaymentsBasePage {
  @ViewChild(CdkVirtualScrollViewport)
  virtualScrollViewport: CdkVirtualScrollViewport;

  readonly PlatformEnum = PlatformEnum;
  readonly SortDirection = SortDirection;
  readonly SortFields = SortFields;
  readonly InvoiceFilterIds = InvoiceFilterIds;
  readonly ColumnNames = TransactionRowDesktopConstants.ColumnNames;

  startDate: Date;
  endDate: Date;
  title: string;
  subTitle: string;
  transactionRowText: TransactionRowText;
  showOrderNumber = false;
  quickFilters: QuickFilter[];
  sortField: SortFields = SortFields.issueDate;
  sortDirection = SortDirection.DESC;
  invoiceFilters: Filter[];
  isInitialLoading = true;

  vm$: Observable<AllInvoicesPageVM>;
  browserDimensions: BrowserDimensions;
  isTouch$: Observable<boolean>;
  isMobile: boolean;
  modal: UsfFilteringModalComponent;
  paymentCustomers$: Subscription;
  allInvoicesPageVMSub$: Subscription;
  maxTransactionsSelectedMessage: string;
  loadingSub$: Subscription;
  filtersAndSortSub$: Subscription;
  featureFlags$: Subscription;
  showShareAndDownload = false;
  showFullPO = false;
  showViewStatementsBtn = false;
  columnSizes: Map<
    TransactionRowDesktopConstants.ColumnNames,
    string | number
  > = new Map();

  constructor(
    public router: Router,
    readonly route: ActivatedRoute,
    readonly paymentsService: PaymentsService,
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    public allInvoicesService: AllInvoicesService,
    private translateService: TranslateService,
    public invoiceIsrService: InvoiceIsrService,
  ) {
    super(router, route, paymentsService);
  }

  ionViewWillEnter(): void {
    super.ionViewWillEnter();
    this.vm$ = this.allInvoicesService.getInvoices$();
    this.handleInitialFilters();

    this.browserDimensions = this.platformService.getBrowserDimensions();
    this.isTouch$ = this.platformService.isTouchExperience$;
    this.isMobile = this.platformService.platformType === PlatformEnum.mobile;
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.allInvoices),
    );
    this.setTranslatedText();
    this.setQuickFilters();

    this.allInvoicesPageVMSub$ = this.vm$.subscribe(vm => {
      this.allInvoicesService.showPaymentsOutageMessageIfNeeded(
        vm.paymentHasOutage,
        this.translateService.instant('i18n.payments.outageMessage'),
      );
    });

    this.loadingSub$ = this.vm$
      .pipe(
        filter(vm => {
          if (!vm.isLoadingTransaction && vm.pageNum === 0) {
            return false;
          }
          return true;
        }),
        distinctUntilChanged(
          (x, y) => x.isLoadingTransaction === y.isLoadingTransaction,
        ),
      )
      .subscribe(vm => {
        this.isInitialLoading = vm.isLoadingTransaction;
        if (!vm.isLoadingTransaction) {
          this.loadingSub$?.unsubscribe();
        }
      });

    this.featureFlags$ = combineLatest([
      this.panAppState.feature$([
        FEATURES.split_user_payments_allInvoices_download.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_user_payments_ar_statements.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_dynamic_payments_order_number.name,
      ]),
    ]).subscribe(([invoicesDownload, viewStatements, orderNumberFlag]) => {
      this.showShareAndDownload = !!invoicesDownload;
      this.showViewStatementsBtn = !!viewStatements;
      this.showOrderNumber = !!orderNumberFlag;
      this.updateColumnSizes();
    });
  }

  initObservables() {
    this.filtersAndSortSub$ = this.allInvoicesService
      .getFiltersAndSortInformation$()
      .pipe(skipWhile(res => res.filters.length === 0))
      .subscribe(res => {
        this.invoiceFilters = res.filters;
      });

    this.handleCustomerFilterChange();
  }

  handleCustomerFilterChange() {
    let initValue = true;
    let lastCustomerNumber: number;

    this.paymentCustomers$ = this.allInvoicesService
      .getPaymentSelectedCustomers()
      .pipe(
        skipWhile(() => !this.invoiceFilters),
        filter(paymentCustomers => paymentCustomers.customers.length > 0),
      )
      .subscribe(paymentCustomers => {
        let currentCustomerNumber = paymentCustomers.customers[0];
        if (!initValue && currentCustomerNumber != lastCustomerNumber) {
          const newFilters = this.invoiceFilters.filter(
            invoiceFilter =>
              invoiceFilter.filterName !== InvoiceFilterIds.Customers,
          );
          this.allInvoicesService.setFilter([
            ...newFilters,
            {
              filterName: InvoiceFilterIds.Customers,
              value: paymentCustomers.customers,
            },
          ]);
          if (paymentCustomers.isSuperUser) {
            this.clearAllSelections();
          }
        }
        lastCustomerNumber = paymentCustomers.customers[0];
        initValue = false;
      });
  }

  handleInitialFilters() {
    const queryFilters = this.getFiltersFromParams();
    const sessionFilters: Filter[] = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE_KEYS.paymentsInvoiceFilters),
    );
    const initFilters = queryFilters.filter(
      invoiceFilter => invoiceFilter.filterName !== InvoiceFilterIds.Customers,
    );
    const customerFilter: number[] | string =
      (queryFilters.find(
        filter => filter.filterName === InvoiceFilterIds.Customers,
      )?.value as number[] | string) ||
      (sessionFilters?.find(
        filter => filter.filterName === InvoiceFilterIds.Customers,
      )?.value as number[]);
    if (customerFilter) {
      initFilters.push({
        filterName: InvoiceFilterIds.Customers,
        value:
          customerFilter === '*'
            ? []
            : (customerFilter as []).map(value => +value),
      });
    }
    this.allInvoicesService
      .setInitialSortAndFilter(initFilters)
      .pipe(take(1))
      .subscribe(() => {
        this.initObservables();
      });
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.paymentsInvoiceFilters);
  }

  ionViewDidEnter(): void {
    this.allInvoicesService.trackPageLoad();
  }

  ionViewWillLeave(): void {
    this.paymentCustomers$.unsubscribe();
    this.allInvoicesPageVMSub$.unsubscribe();
    this.filtersAndSortSub$.unsubscribe();
    this.featureFlags$.unsubscribe();
    this.loadingSub$?.unsubscribe();
  }

  setQuickFilters() {
    this.quickFilters = [
      {
        id: [Status.OVERDUE],
        title: 'Overdue',
        count: 0,
        color: 'red',
        selected: false,
        translationKey: 'i18n.payments.quickFilters.overdue',
      },
      {
        id: [Status.UNPAID, Status.UNUSED],
        title: 'Unpaid / Unused',
        count: 0,
        color: 'red',
        selected: false,
        translationKey: 'i18n.payments.quickFilters.unpaidUnused',
      },
      {
        id: [Status.PARTIALLY_PAID],
        title: 'Partially paid',
        count: 0,
        color: 'green',
        selected: false,
        translationKey: 'i18n.payments.quickFilters.partiallyPaid',
      },
      {
        id: [Status.PAID, Status.APPLIED],
        title: 'Paid/Applied',
        count: 0,
        color: 'green',
        selected: false,
        translationKey: 'i18n.payments.quickFilters.paidApplied',
      },
    ];
  }

  setTranslatedText() {
    this.translateService
      .get('i18n.payments.allInvoices.title')
      .pipe(take(1))
      .subscribe(_ => {
        this.title = this.translateService.instant(
          'i18n.payments.allInvoices.title',
        );
        this.subTitle = this.translateService.instant(
          'i18n.payments.allInvoices.subTitle',
          { numberOfInvoices: 7 },
        );
        this.transactionRowText = {
          invoice: this.translateService.instant(
            'i18n.payments.transactionRow.invoice',
          ),
          custPo: this.translateService.instant(
            'i18n.payments.transactionRow.custPo',
          ),
          issueDate: this.translateService.instant(
            'i18n.payments.transactionRow.issueDate',
          ),
          issueDateCut: this.translateService.instant(
            'i18n.payments.transactionRow.issueDateCut',
          ),
          amount: this.translateService.instant(
            'i18n.payments.transactionRow.amount',
          ),
          dueDate: this.translateService.instant(
            'i18n.payments.transactionRow.dueDate',
          ),
          amountDue: this.translateService.instant(
            'i18n.payments.transactionRow.amountDue',
          ),
          dueDateCut: this.translateService.instant(
            'i18n.payments.transactionRow.dueDateCut',
          ),
          amountCut: this.translateService.instant(
            'i18n.payments.transactionRow.amountCut',
          ),
          amountDueCut: this.translateService.instant(
            'i18n.payments.transactionRow.amountDueCut',
          ),
          pay: this.translateService.instant('i18n.common.pay'),
          paymentAmount: this.translateService.instant(
            'i18n.payments.transactionRow.paymentAmount',
          ),
        };
        this.maxTransactionsSelectedMessage = this.translateService.instant(
          'i18n.payments.allInvoices.maximumInvoicesSelected',
          { maximumSelected: MAX_INVOICE_SELECTION },
        );
      });
  }

  scrolledIndexChange(
    _event,
    pageNum: number,
    hasNextPage: boolean,
    isLoading: boolean,
  ) {
    if (isLoading) return;
    const end = this.virtualScrollViewport.getRenderedRange().end;
    const total = this.virtualScrollViewport.getDataLength();
    const nextPage = pageNum + 1;

    if (end === total && hasNextPage) {
      this.paymentsService.fetchInvoices(nextPage);
    }
  }

  handleSort({
    sortField,
    sortDirection,
  }: {
    sortField: string;
    sortDirection: SortDirection;
  }) {
    this.sort(sortField as SortFields, sortDirection);
  }

  sort(field: SortFields, direction: SortDirection = SortDirection.DESC) {
    if (field === this.sortField) {
      this.sortDirection =
        this.sortDirection === SortDirection.ASC
          ? SortDirection.DESC
          : SortDirection.ASC;
    } else {
      this.sortDirection = direction;
    }
    this.sortField = field;

    let sortFields = [{ fieldName: field, sortValue: this.sortDirection }];
    if (this.sortField !== SortFields.transaction) {
      sortFields.push({
        fieldName: SortFields.transaction,
        sortValue: SortDirection.DESC,
      });
    }

    this.allInvoicesService.setSort(sortFields);
  }

  trackByIdx(i) {
    return i;
  }

  selectTransactionRow(transactionVM: PaymentTransactionsViewModel) {
    this.vm$.pipe(take(1)).subscribe(vm => {
      if (!transactionVM.selected && vm.isMaxTransactionsSelected) {
        this.paymentsService.showToastMessage(
          this.maxTransactionsSelectedMessage,
        );
      } else {
        this.paymentsService.selectInvoice(transactionVM);
      }
    });
  }

  async selectAllInvoices() {
    const vm = await firstValueFrom(this.vm$);

    this.paymentsService.selectAllInvoices(
      this.maxTransactionsSelectedMessage,
      null,
      MAX_INVOICE_SELECTION - vm.selectedTransactions.length,
    );
  }

  clearAllSelections() {
    this.paymentsService.clearSelectedInvoices();
  }

  downloadSelectedInvoices(transactions: PaymentTransactionsViewModel[]) {
    this.invoiceIsrService.mapInvoicesToShow(transactions);
  }

  resetFilters() {
    this.sortField = SortFields.issueDate;
    this.sortDirection = SortDirection.DESC;
  }

  async openMoreOptions(optionObject: {
    event: Event;
    transaction: PaymentTransactionsViewModel;
  }) {
    this.invoiceIsrService.openInvoiceOptions(optionObject);
  }

  viewInvoice(transaction: PaymentTransactionsViewModel) {
    this.invoiceIsrService.viewInvoice(
      transaction.isr.docID.toString(),
      transaction.transactionNumber,
      transaction.secondaryTransactionNumber,
    );
  }

  downloadAllInvoicesPage() {
    this.paymentsService.fetchInvoices(1, 0);

    this.vm$.pipe(skip(1), take(1)).subscribe(vm => {
      if (vm.warning?.isWarning) {
        this.paymentsService.showToastMessage(
          vm.warning.messageText.toString(),
        );
        this.paymentsService.fetchInvoices(1, undefined);
        return;
      }
      this.allInvoicesService.downloadAllInvoices(vm.transactions);
    });
  }

  openShareDownload(transactions: PaymentTransactionsViewModel[]) {
    const buttons: any[] = [
      {
        text: this.translateService.instant('i18n.common.download'),
        handler: () => {
          this.downloadAllInvoicesPage();
          this.invoiceIsrService.dismissPopover();
        },
      },
      {
        text: this.translateService.instant('i18n.common.share'),
        handler: () => {
          this.shareAllInvoices();
          this.invoiceIsrService.dismissPopover();
        },
      },
      {
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
        handler: () => {},
      },
    ];

    this.allInvoicesService.presentPaymentOptions(buttons, event);
  }

  shareAllInvoices() {
    this.paymentsService.fetchInvoices(1, 0);
    this.vm$.pipe(skip(1), take(1)).subscribe(vm => {
      if (vm.warning?.isWarning) {
        this.paymentsService.showToastMessage(
          vm.warning.messageText.toString(),
        );
        this.paymentsService.fetchInvoices(1, undefined);
        return;
      }
      const issueDates = this.getIssueDates(vm);
      this.paymentsService.shareInvoices(
        this.title,
        vm.transactions,
        this.convertFilterDate(issueDates.fromIssueDate),
        this.convertFilterDate(issueDates.toIssueDate),
      );
    });
  }

  printAllInvoices() {
    this.paymentsService.fetchInvoices(1, 0);
    this.vm$.pipe(skip(1), take(1)).subscribe(vm => {
      if (vm.warning?.isWarning) {
        this.paymentsService.showToastMessage(
          vm.warning.messageText.toString(),
        );
        this.paymentsService.fetchInvoices(1, undefined);
        return;
      }
      const issueDates = this.getIssueDates(vm);
      this.paymentsService.printInvoices(
        this.title,
        vm.transactions,
        this.convertFilterDate(issueDates.fromIssueDate),
        this.convertFilterDate(issueDates.toIssueDate),
      );
    });
  }

  poNumberClicked() {
    this.showFullPO = !this.showFullPO;
    this.updateColumnSizes();
  }

  updateColumnSizes() {
    this.columnSizes = TransactionRowDesktopConstants.columnSizes(
      false,
      false,
      true,
      this.showFullPO,
      this.showOrderNumber,
    );
  }

  goToMakeAPayment() {
    this.clearAllSelections();
    this.routeTo(PATHS.PAYMENTS_MAKE_PAYMENT);
  }

  gotToARStatements() {
    this.router.navigate([PATHS.PAYMENTS_STATEMENTS]);
  }

  goToARStatementsWithCustomers() {
    this.vm$.pipe(take(1)).subscribe(vm => {
      const customersFilter = vm.filters.find(
        filter => filter.filterName === InvoiceFilterIds.Customers,
      );
      if (customersFilter) {
        sessionStorage.setItem(
          SESSION_STORAGE_KEYS.paymentsInvoiceFilters,
          JSON.stringify([customersFilter]),
        );
      }
      this.gotToARStatements();
    });
  }
}
